import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { FeatureFlagsProvider } from "./lib/featureFlags";
import { ThemeProvider } from "./lib/theme";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Projects from "./pages/Projects";
import SuperAdmin from "./pages/SuperAdmin";
import Workspace from "./pages/Workspace";
import DesignReviewLanding from "./pages/DesignReviewLanding";
import Engineering from "./pages/Engineering";
import DocumentsRegister from "./pages/DocumentsRegister";
import ReviewerInbox from "./pages/ReviewerInbox";
import ProjectDashboard from "./pages/ProjectDashboard";
import RevisionComparison from "./pages/RevisionComparison";

/** Legacy redirect — the standalone /master-plan route is removed.
 *  We funnel users into the Stage Workspace and auto-activate A.5.1
 *  (the Master Plan output deliverable). The pinned A0 sidebar button
 *  uses the same code path. */
function MasterPlanRedirect() {
  const { id } = useParams();
  React.useEffect(() => {
    try { sessionStorage.setItem("rachana.stage.activate", "A.5.1"); } catch (e) {}
  }, []);
  return <Navigate to={`/projects/${id}`} replace />;
}
import ErrorBoundary from "./components/ErrorBoundary";

const BRAND_TITLE = "Capexara Process Industry · AI based Engineering & Design";

/**
 * The Emergent platform script (emergent-main.js, loaded from index.html)
 * rewrites the document title to "Emergent | Fullstack App" shortly after
 * page load. We observe the <title> node and re-assert the Capexara Process Industry title
 * whenever anything else tries to set it.
 */
function useBrandTitle() {
  useEffect(() => {
    const ensureTitle = () => {
      if (document.title !== BRAND_TITLE) {
        document.title = BRAND_TITLE;
      }
    };
    ensureTitle();
    const titleEl = document.querySelector("head > title");
    if (!titleEl) return;
    const observer = new MutationObserver(ensureTitle);
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    // Also guard against late script swaps that replace the <title> element entirely.
    const headObserver = new MutationObserver(ensureTitle);
    headObserver.observe(document.head, { childList: true });
    return () => {
      observer.disconnect();
      headObserver.disconnect();
    };
  }, []);
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div style={{ padding: 48, color: "var(--pf-text-muted)" }}>Loading…</div>;
  if (!user || !user.id) return <Navigate to="/login" replace />;
  // Force admin-invited users to change their temp password before
  // accessing anything else.
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function App() {
  useBrandTitle();
  return (
    <div className="App">
      <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <FeatureFlagsProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
              <Route path="/projects" element={<Protected><Projects /></Protected>} />
              <Route path="/design-review" element={<Protected><DesignReviewLanding /></Protected>} />
              <Route path="/superadmin" element={<Protected><SuperAdmin /></Protected>} />
              <Route path="/reviewer/inbox" element={<Protected><ReviewerInbox /></Protected>} />
              <Route path="/projects/:id" element={<Protected><Workspace /></Protected>} />
              <Route path="/projects/:id/dashboard" element={<Protected><ProjectDashboard /></Protected>} />
              <Route path="/projects/:id/comparison" element={<Protected><RevisionComparison /></Protected>} />
              <Route path="/projects/:id/master-plan" element={<Protected><MasterPlanRedirect /></Protected>} />
              <Route path="/projects/:id/documents" element={<Protected><DocumentsRegister /></Protected>} />
              <Route path="/projects/:id/engineering" element={<Protected><Engineering /></Protected>} />
              <Route path="/projects/:id/engineering/:section" element={<Protected><Engineering /></Protected>} />
              <Route path="/projects/:id/engineering/:section/:tab" element={<Protected><Engineering /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </FeatureFlagsProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
